import type { StatisticsFilter, StatisticsFilterType } from "@/lib/statistics-api.service";
import type { StatisticsFilters } from "./StatisticsControls";
import { Toast } from "@/lib/utils";
import { domToPng } from "modern-screenshot";
import domtoimage from "dom-to-image";
import html2canvas from "html2canvas";

const formatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const formatNumber = (n: number): string => {
	return formatter.format(n);
};

export const COLORS = [
	"#0088FE",
	"#00C49F",
	"#FFBB28",
	"#FF8042",
	"#8884D8",
	"#82CA9D",
	"#FFC658",
	"#FF7C7C",
	"#8DD1E1",
	"#D084D0",
];

/** Chỉ hai field của thống kê theo ngành mà việc đặt nhãn cần đến. */
export interface MajorLabelSource {
	department?: string;
	departmentCode?: string;
}

/**
 * Trả về hàm đặt nhãn cho một ngành: tên ngành, kèm mã KHI VÀ CHỈ KHI tên đó
 * trùng với một ngành khác trong cùng tập dữ liệu.
 *
 * Tên ngành không phải khoá phân biệt. Khi khoá tuyển đổi mã, cùng một ngành tồn
 * tại hai hàng catalog cùng tên — `7480101TT` (2026) và `7480101_TT` (2025) đều
 * là "Khoa học máy tính (Tiên tiến)" và cả hai đang có sinh viên. Hai cột biểu đồ
 * cùng nhãn thì người xem không biết cột nào là khoá nào, mất đúng thứ mà việc
 * hiện tên định đem lại.
 *
 * Chỉ gắn mã cho ngành thật sự trùng: gắn cho tất cả sẽ làm mọi nhãn dài thêm mà
 * không phân biệt thêm được gì. Dạng `Tên (mã)` khớp với cách server đặt tên cột
 * trùng trong breakdown theo tỉnh/trường.
 *
 * Thiếu tên thì lùi về mã — một ngành có trong dữ liệu sinh viên nhưng chưa có
 * trong catalog vẫn phải nhận diện được.
 */
export function createMajorLabeler(
	stats: readonly MajorLabelSource[],
): (dept?: MajorLabelSource) => string {
	const counts = new Map<string, number>();
	for (const dept of stats) {
		if (dept?.department) counts.set(dept.department, (counts.get(dept.department) ?? 0) + 1);
	}

	return (dept) => {
		const name = dept?.department;
		if (!name) return dept?.departmentCode || "Ngành không xác định";
		return (counts.get(name) ?? 0) > 1 ? `${name} (${dept?.departmentCode})` : name;
	};
}

/**
 * Ngắt một nhãn dài thành nhiều dòng theo RANH GIỚI TỪ, để trục biểu đồ đọc
 * được mà chữ vẫn nằm ngang.
 *
 * Tồn tại vì trục ngành đổi từ mã (`7480101`) sang tên đầy đủ ("Công nghệ thông
 * tin (Chương trình tiên tiến)") — dài gấp nhiều lần và không thể nhồi vào một
 * dòng. Hướng xử lý là ngắt dòng chứ không xoay chữ: chữ xoay chéo đọc chậm hơn
 * và là thứ mọi hướng dẫn trực quan hoá dữ liệu đều khuyên tránh; bề ngang cần
 * thiết lấy từ việc cho biểu đồ cuộn ngang, không lấy từ việc bóp nhỏ nó.
 *
 * Một từ đơn dài hơn `maxChars` bị cắt cứng giữa từ. Không đẹp, nhưng để nó
 * tràn ra sẽ chồng lên nhãn bên cạnh và làm mất chữ của cả hai.
 *
 * Quá `maxLines` thì cắt và thêm "…": một nhãn cao mãi sẽ đẩy vùng vẽ của biểu
 * đồ co lại, đúng thứ cần tránh. Tên đầy đủ vẫn còn nguyên ở card và ở bảng.
 */
export function wrapLabel(text: string, maxChars = 14, maxLines = 3): string[] {
	const words = String(text ?? "")
		.split(/\s+/)
		.filter(Boolean)
		.flatMap((word) => {
			if (word.length <= maxChars) return [word];
			const pieces: string[] = [];
			for (let i = 0; i < word.length; i += maxChars) pieces.push(word.slice(i, i + maxChars));
			return pieces;
		});

	const lines: string[] = [];
	let current = "";
	for (const word of words) {
		const next = current ? `${current} ${word}` : word;
		if (next.length > maxChars && current) {
			lines.push(current);
			current = word;
		} else {
			current = next;
		}
	}
	if (current) lines.push(current);

	if (lines.length <= maxLines) return lines;
	const kept = lines.slice(0, maxLines);
	kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(0, maxChars - 1))}…`;
	return kept;
}

export const takeScreenshot = async (
	elementRef: React.RefObject<HTMLDivElement | null>,
	filename: string,
) => {
	if (!elementRef.current) return;

	await new Promise((resolve) => setTimeout(resolve, 1000));

	const downloadImage = (dataUrl: string, filename: string) => {
		const link = document.createElement("a");
		link.download = filename;
		link.href = dataUrl;
		link.click();
	};

	const convertBlobToDataUrl = (blob: Blob): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	};

	// Method 1: Try modern-screenshot
	try {
		console.log("Attempting screenshot with modern-screenshot...");
		const dataUrl = await domToPng(elementRef.current, {
			quality: 1,
			scale: 2,
			backgroundColor: "#ffffff",
		});
		downloadImage(dataUrl, `${filename}.png`);
		console.log("Screenshot successful with modern-screenshot");
		return;
	} catch (error) {
		console.warn("modern-screenshot failed:", error);
	}

	// Method 2: Try dom-to-image
	try {
		console.log("Attempting screenshot with dom-to-image...");
		const dataUrl = await domtoimage.toPng(elementRef.current, {
			quality: 1.0,
			bgcolor: "#ffffff",
			style: {
				transform: "scale(1)",
				transformOrigin: "top left",
			},
		});
		downloadImage(dataUrl, `${filename}.png`);
		console.log("Screenshot successful with dom-to-image");
		return;
	} catch (error) {
		console.warn("dom-to-image failed:", error);
	}

	// Method 3: Try html2canvas
	try {
		console.log("Attempting screenshot with html2canvas...");
		const canvas = await html2canvas(elementRef.current, {
			backgroundColor: "#ffffff",
			scale: 2,
			logging: false,
			useCORS: true,
			allowTaint: true,
			foreignObjectRendering: true,
			removeContainer: true,
			imageTimeout: 15000,
		});

		canvas.toBlob(
			(blob) => {
				if (blob) {
					convertBlobToDataUrl(blob).then((dataUrl) => {
						downloadImage(dataUrl, `${filename}.png`);
						console.log("Screenshot successful with html2canvas");
					});
				}
			},
			"image/png",
			1.0,
		);
		return;
	} catch (error) {
		console.warn("html2canvas failed:", error);
	}

	// Throw rather than `alert`: the caller needs to know all three capture
	// methods failed so it can say so, and `alert` blocks the page while giving
	// no caller a chance to react.
	console.error("All screenshot methods failed");
	throw new Error("Không tạo được ảnh biểu đồ");
};

/**
 * Downloads a DOM region as a PNG, toasting all three states.
 *
 * Never rejects: the failure has already been reported through a toast, so a
 * button can simply `await` this to know when to stop its spinner without
 * needing a catch of its own.
 */
export const downloadChartPng = async (
	elementRef: React.RefObject<HTMLDivElement | null>,
	filename: string,
): Promise<void> => {
	const task = takeScreenshot(elementRef, filename);
	Toast.promise(task, {
		loading: "Đang tạo ảnh biểu đồ...",
		success: `Đã tải ${filename}.png`,
		error: "Không tạo được ảnh. Thử lại sau khi biểu đồ vẽ xong.",
	});
	try {
		await task;
	} catch {
		// Toast.promise already showed the error; swallow it here so it does not
		// surface as an unhandled rejection.
	}
};

/**
 * Turns the filter bar's state into the shape the API takes.
 *
 * The khoa, the date range and the appointment slot all live in one UI object;
 * every request and every CSV download has to carry all three, or a tab shows
 * one khoa's numbers while its export contains every khoa.
 */
export function toAPIFilter(
	filters: StatisticsFilters | undefined,
	type: StatisticsFilterType,
): StatisticsFilter {
	const filter: StatisticsFilter = { type };

	if (filters?.facultyCode) filter.facultyCode = filters.facultyCode;
	if (filters?.receptionDateRange?.from) filter.receptionDateFrom = filters.receptionDateRange.from;
	if (filters?.receptionDateRange?.to) filter.receptionDateTo = filters.receptionDateRange.to;
	if (filters?.appointmentSlot) filter.appointmentSlot = filters.appointmentSlot;
	// Khoá tuyển sinh. Bỏ sót ở đây là lỗi im lặng tệ nhất của cả bộ lọc: Select
	// đổi, state đổi, cache key phía server đã tính theo khoá — nhưng tham số
	// không rời khỏi trình duyệt, nên tab vẫn hiện số của mọi khoá. Không có gì
	// trên giao diện cho biết là sai.
	if (filters?.admissionYear) filter.admissionYear = filters.admissionYear;

	return filter;
}

/**
 * Nhãn hiển thị của một tổ hợp: "A00 - Toán; Vật lí; Hóa học".
 *
 * Không có mã thì trả về nguyên danh sách môn, KHÔNG chèn dấu gạch treo lửng
 * hay chữ "N/A" — một tổ hợp không nằm trong bảng của Bộ vẫn là dữ liệu thật,
 * chỉ là không có tên gọi tắt.
 */
export function formatCombinationLabel(combination: string, code: string | null): string {
	return code ? `${code} - ${combination}` : combination;
}
